/**
 * Client-side SLA state.
 *
 * The server sends a snapshot with every ticket, but that snapshot ages the
 * moment it arrives. Rather than poll, the SPA recomputes locally each second
 * using @helpdesk/sla -- the *same module the API uses* -- so the countdown an
 * agent watches can never drift from the number the server sorted their queue
 * by. That shared package is the reason this file is thin.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  calendarWithHolidays,
  slaSnapshot,
  type BusinessCalendar,
  type SlaSnapshot,
} from "@helpdesk/sla";

import { api } from "./api";
import { useAwayPauses } from "./away";
import { queryKeys } from "./queryKeys";
import type { Pipeline, TicketSummary } from "../types";

/**
 * The business calendar, fetched once and cached hard.
 *
 * Opening hours and bank holidays change a few times a year; refetching them
 * per render would be a request per ticket row.
 */
export function useBusinessCalendar(): BusinessCalendar {
  const { data } = useQuery({
    queryKey: queryKeys.businessHours,
    queryFn: api.businessHours,
    staleTime: 60 * 60_000,
  });

  return useMemo(() => {
    if (!data) return calendarWithHolidays([]);
    return {
      timeZone: data.timeZone,
      week: data.week,
      holidays: data.holidays.map((holiday) => holiday.date),
    };
  }, [data]);
}

/**
 * A clock that ticks once a second, shared by every SLA indicator on screen.
 *
 * One interval for the whole page rather than one per row: a 200-row list would
 * otherwise schedule 200 timers that all fire in the same second.
 */
let subscribers = new Set<(now: Date) => void>();
let timer: ReturnType<typeof setInterval> | undefined;

function subscribe(callback: (now: Date) => void): () => void {
  subscribers.add(callback);
  if (!timer) {
    timer = setInterval(() => {
      const now = new Date();
      for (const subscriber of subscribers) subscriber(now);
    }, 1000);
  }
  return () => {
    subscribers.delete(callback);
    if (subscribers.size === 0 && timer) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}

export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => subscribe(setNow), []);
  return now;
}

/** Recompute a ticket's SLA position against the current second. */
export function useLiveSla(
  ticket: Pick<
    TicketSummary,
    "awaitingResponseSince" | "status" | "pipelineId" | "assigneeId" | "sla"
  >,
  pipelines: Pipeline[],
  calendar: BusinessCalendar,
): SlaSnapshot {
  const now = useNow();
  /*
   * From context rather than from a prop, unlike the calendar beside it. The
   * reasoning is in lib/away.tsx: this is read one level deeper than the
   * calendar is, by the hook itself rather than by the component calling it.
   */
  const away = useAwayPauses();

  return useMemo(() => {
    // Mirrors SlaService.snapshot: a closed ticket owes no response, so its
    // clock is not shown even if a customer message is technically unanswered.
    const resolved = ticket.status === "closed";
    const clockStartedAt =
      resolved || !ticket.awaitingResponseSince
        ? null
        : new Date(ticket.awaitingResponseSince);

    const target =
      pipelines.find((pipeline) => pipeline.id === ticket.pipelineId)?.slaMinutes ??
      null;

    return slaSnapshot({
      clockStartedAt,
      targetMinutes: target,
      now,
      calendar,
      /* The assignee's own away periods -- the same rule the server applies,
         because an unassigned ticket is nobody's to be away from. */
      pauses:
        ticket.assigneeId === null ? [] : (away.get(ticket.assigneeId) ?? []),
    });
  }, [
    ticket.awaitingResponseSince,
    ticket.status,
    ticket.pipelineId,
    ticket.assigneeId,
    pipelines,
    calendar,
    away,
    now,
  ]);
}

/**
 * Format remaining business minutes as a countdown.
 *
 * Deliberately shows minutes rather than a wall-clock time: the number is
 * business minutes, and "due 09:14 tomorrow" invites the reader to subtract it
 * from now and get a wrong answer across an overnight or a weekend.
 *
 * Time remaining rounds UP and time overdue rounds DOWN, so the printed number
 * never contradicts the colour beside it. Flooring both would show "15m" in
 * green on a 30-minute target -- exactly half, which the amber rule says should
 * already have turned -- because the level tests the true 15.4 while the label
 * showed 15. Rounding away from the threshold keeps the two consistent.
 */
export function formatRemaining(minutes: number | null): string {
  if (minutes === null) return "—";

  const overdue = minutes < 0;
  const total = overdue ? Math.floor(-minutes) : Math.ceil(minutes);
  const hours = Math.floor(total / 60);
  const mins = total % 60;

  /*
   * Days once hours stop being a number anybody reads.
   *
   * "504h 00m over" is not a duration, it is arithmetic homework -- and it
   * appeared wherever a sales call had been sitting for a fortnight, which is
   * exactly the row somebody is meant to read at a glance. Two digits of hours
   * is about the limit, so the unit changes at 48, matching `describeOver` in
   * `AttentionBadge` so the row and its own tooltip agree.
   *
   * Only ever one unit past that point. "3d 7h" is no more actionable than
   * "3d" on a window measured in days, and the extra characters are what push
   * a countdown onto a second line in a narrow column.
   */
  const body =
    hours >= 48
      ? `${Math.floor(hours / 24)}d`
      : hours > 0
        ? `${hours}h ${String(mins).padStart(2, "0")}m`
        : `${mins}m`;

  return overdue ? `${body} over` : body;
}

/** Tailwind text colour for a risk level. */
export function slaTextClass(level: SlaSnapshot["level"]): string {
  if (level === "breached") return "text-sla-breached";
  if (level === "warning") return "text-sla-warning";
  if (level === "ok") return "text-sla-ok";
  return "text-faint";
}
